<template>
  <div class="p-4">
    <div class="flex items-center justify-between gap-4">
      <Bs2Title :title="pageTitle" :subtitle="pageSubtitle" />
      <div class="flex gap-2">
        <ButtonDaisy text="Retour" variant="ghost" size="sm" :icon="ArrowLeftIcon" @click="router.back()" />
        <ButtonDaisy
          text="Actualiser"
          variant="outline"
          size="sm"
          :icon="ArrowPathIcon"
          :loader="isLoading"
          @click="fetchArbitrations"
        />
      </div>
    </div>

    <div class="mt-6 flex flex-nowrap gap-4 overflow-x-auto py-4">
      <CounterCard
        v-for="status in arbitrationStatusOptions"
        :key="status.value"
        :counter="status"
        :status-counts="arbitrationStatusCounts"
        :status-type="'arbitration'"
        :class="[
          'flex-1 min-w-[100px] shrink-0 self-stretch my-1',
          { 'ring-2 ring-primary ring-offset-2': selectedStatusFilter === status.value },
        ]"
        @click="onCounterCardClick(status.value)"
      />
    </div>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body p-4">
        <div class="flex flex-row flex-wrap items-end gap-2 justify-end">
          <TextInput
            v-model="searchQuery"
            label="Recherche"
            placeholder="N° devis, société, demandeur"
            class="flex-1 min-w-[200px] max-w-full sm:max-w-[300px]"
          />
          <SelectInput
            v-model="filterDateCreation"
            label="Date création"
            :options="[{ id: 'toutes-creation', value: null, label: 'Toutes' }, ...dateFilterOptions]"
            placeholder="Année/mois"
            class="flex-1 min-w-[150px] max-w-full sm:max-w-[200px]"
          />
        </div>
      </div>
    </div>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body p-4 pt-2">
        <GridVue
          :list="arbitrationStore.arbitrations || []"
          :cols="gridColumns"
          :filter-model="gridFilterModel"
          :get-row-height="() => 48"
          :use-server-side-pagination="true"
          :pagination-page-size="pageSize"
          :pagination-total-rows="arbitrationStore.total || 0"
          :current-page="page"
          :total-pages="arbitrationStore.totalPages || 1"
          :show-export="false"
          @onFilterChanged="updateFilter"
          @onPaginationChanged="onPaginationChanged"
        >
          <template #title>
            <h2 class="card-title">Arbitrages BU</h2>
          </template>
        </GridVue>
      </div>
    </div>

    <ArbitrationDetailModal
      :arbitration="selectedArbitration"
      :is-open="isDetailModalOpen"
      :on-arbitration-updated="onArbitrationUpdated"
      @close="isDetailModalOpen = false"
      @updated="onArbitrationUpdated"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, provide, ref, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import ButtonDaisy from '@/components/common/buttons/ButtonDaisy.vue'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import CounterCard from '@/components/common/ui/CounterCard.vue'
import GridVue from '@/components/data-visualization/grids/GridVue.vue'
import ArbitrationBadgeGrid from '@/components/data-visualization/grids/ArbitrationBadgeGrid.vue'
import ArbitrationActionsCell from '@/components/features/arbitrations/ArbitrationActionsCell.vue'
import ArbitrationDevisLinkCell from '@/components/features/arbitrations/ArbitrationDevisLinkCell.vue'
import ArbitrationDetailModal from '@/components/features/arbitrations/ArbitrationDetailModal.vue'
import SelectInput from '@/components/common/selects/SelectInput.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import { ArrowLeftIcon, ArrowPathIcon } from '@heroicons/vue/24/outline'
import { useBoostCorePermissionStore } from '@/stores/useBoostCorePermissionStore'
import { useCompanyArbitrationStore } from '@/stores/useCompanyArbitrationStore'
import { formatDate, dateComparator } from '@/utils/dateUtils'
import type { FilterModel, ITextFilterParams, IDateFilterParams } from 'ag-grid-community'

const router = useRouter()
const route = useRoute()
const boostCoreStore = useBoostCorePermissionStore()
const arbitrationStore = useCompanyArbitrationStore()

const isLoading = ref(false)
const page = ref(1)
const pageSize = ref(20)
const statusFilter = ref('')
const filters = ref<Record<string, any>>({})
const searchQuery = ref<string>('')
const selectedStatusFilter = ref<string | null>(null)
const filterDateCreation = ref<string | null>(null)
const selectedArbitration = ref<any>(null)
const isDetailModalOpen = ref(false)

let timeout: any = null

const canValidate = computed(
  () => boostCoreStore.include('ARBITRATION_VALIDATE') || boostCoreStore.include('SUPER_ADMIN'),
)

const pageTitle = computed(() => 'Arbitrages BU')
const pageSubtitle = computed(() =>
  canValidate.value ? 'Affiche les arbitrages à valider sur votre BU (source ou cible).' : 'Affiche vos propres arbitrages.',
)

const dateFilterOptions = computed(() => {
  const now = new Date()
  const opts: { id: string; value: string; label: string }[] = []
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    const label = d.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })
    opts.push({ id: val, value: val, label })
  }
  return opts
})

const arbitrationStatusOptions = computed(() => [
  { value: 'PENDING', label: 'En attente', order: 1 },
  { value: 'APPROVED', label: 'Approuvé', order: 2 },
  { value: 'REJECTED', label: 'Rejeté', order: 3 },
])

const arbitrationStatusCounts = computed(() => {
  const counts = arbitrationStore.statusCounts
  const map: Record<string, { count: number; sumMarge: number }> = {
    PENDING: { count: 0, sumMarge: 0 },
    APPROVED: { count: 0, sumMarge: 0 },
    REJECTED: { count: 0, sumMarge: 0 },
  }
  arbitrationStatusOptions.value.forEach((option) => {
    const statusData = counts?.[option.value]
    if (statusData) map[option.value].count = statusData || 0
  })
  return map
})

const gridColumns = computed(() => [
  {
    field: 'requester',
    headerName: 'Demandeur',
    cellClass: 'border-0',
    valueGetter: (params: any) => {
      const r = params.data?.requester
      return r ? [r.firstName, r.lastName].filter(Boolean).join(' ') : '-'
    },
    filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
  },
  {
    field: 'requesterBu',
    headerName: 'BU demandée',
    cellClass: 'border-0',
    valueGetter: (params: any) => params.data?.requesterBu || '-',
    filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
  },
  {
    field: 'company',
    headerName: 'Société',
    cellClass: 'border-0',
    valueGetter: (params: any) => params.data?.company?.name || '-',
    filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
  },
  {
    field: 'workforce',
    headerName: 'Effectif',
    cellClass: 'border-0',
    filter: 'agNumberColumnFilter',
    valueGetter: (params: any) => params.data?.companyWorkforce ?? params.data?.company?.workforce,
    valueFormatter: (params: any) => (params.value == null ? '-' : String(params.value)),
  },
  {
    field: 'assignedCommercial',
    headerName: 'Commercial assigné',
    cellClass: 'border-0',
    valueGetter: (params: any) => {
      const a = params.data?.company?.assignedCommercial
      return a ? [a.firstName, a.lastName].filter(Boolean).join(' ') : '-'
    },
    filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
  },
  {
    field: 'quoteRef',
    headerName: 'Devis',
    cellClass: 'border-0',
    cellRenderer: ArbitrationDevisLinkCell,
    valueGetter: (params: any) => params.data?.triggeringOrder?.quote?.quoteReference || '-',
    filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
  },
  {
    field: 'createdAt',
    headerName: 'Date création',
    cellClass: 'border-0',
    filter: 'agDateColumnFilter',
    valueFormatter: (params: any) => formatDate(params.value?.toString() || ''),
    comparator: dateComparator,
    filterParams: { filterOptions: ['equals', 'inRange', 'lessThan', 'greaterThan'], maxNumConditions: 1 } as IDateFilterParams,
  },
  {
    field: 'status',
    headerName: 'Statut',
    cellClass: 'border-0',
    cellRenderer: ArbitrationBadgeGrid,
    valueGetter: (params: any) => params.data?.status,
    filterParams: { filterOptions: ['contains'], maxNumConditions: 1 } as ITextFilterParams,
  },
  {
    field: 'actions',
    headerName: 'Actions',
    resizable: false,
    filter: false,
    minWidth: 100,
    maxWidth: 100,
    cellRenderer: ArbitrationActionsCell,
    sortable: false,
    cellClass: 'border-0 grid-action',
  },
])

const gridFilterModel = computed(() => {
  const f = filters.value
  const model: Record<string, any> = {}
  if (f.status) model.status = f.status
  return Object.keys(model).length ? model : undefined
})

function localYmd(d: Date): string {
  const y = d.getFullYear()
  const mo = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${mo}-${day}`
}

function buildMonthDateFilter(yearMonth: string | null) {
  if (!yearMonth || !/^\d{4}-\d{2}$/.test(yearMonth)) return undefined
  const [y, m] = yearMonth.split('-').map(Number)
  const start = new Date(y, m - 1, 1)
  const end = new Date(y, m, 0)
  return { filterType: 'date', type: 'inRange', dateFrom: localYmd(start), dateTo: localYmd(end) }
}

function onCounterCardClick(statusValue: string) {
  if (selectedStatusFilter.value === statusValue) {
    selectedStatusFilter.value = null
    statusFilter.value = ''
    const { status: _s, ...rest } = filters.value
    filters.value = rest
  } else {
    selectedStatusFilter.value = statusValue
    statusFilter.value = statusValue
    filters.value = { ...filters.value, status: { filterType: 'text', filter: statusValue, type: 'equals' } }
  }
  page.value = 1
  fetchArbitrations()
}

const fetchArbitrations = async () => {
  isLoading.value = true
  try {
    let statusToFilter: string | undefined
    if (filters.value.status?.filter) statusToFilter = filters.value.status.filter
    else if (statusFilter.value) statusToFilter = statusFilter.value

    const options = {
      page: page.value,
      pageSize: pageSize.value,
      status: statusToFilter,
      search: searchQuery.value || undefined,
      createdAt: buildMonthDateFilter(filterDateCreation.value),
      orderBy: 'createdAt',
      order: 'desc',
    }

    if (canValidate.value) {
      await arbitrationStore.fetchArbitrationsToValidate(options)
    } else {
      await arbitrationStore.fetchMyArbitrations(options)
    }
  } catch (error) {
    console.error('Error fetching arbitrations:', error)
  } finally {
    isLoading.value = false
  }
}

async function updateFilter(filtersModel: FilterModel) {
  const newFilters: Record<string, any> = {}
  if (filtersModel.status) {
    if (filtersModel.status.filter) {
      newFilters.status = { filterType: 'text', filter: filtersModel.status.filter, type: 'equals' }
      selectedStatusFilter.value = filtersModel.status.filter
    } else if (filtersModel.status.value) {
      newFilters.status = { filterType: 'text', filter: filtersModel.status.value, type: 'equals' }
      selectedStatusFilter.value = filtersModel.status.value
    }
  } else {
    selectedStatusFilter.value = null
  }
  Object.keys(filtersModel).forEach((key) => {
    if (key !== 'status' && filtersModel[key]) newFilters[key] = filtersModel[key]
  })
  filters.value = newFilters
  page.value = 1
  await fetchArbitrations()
}

const onPaginationChanged = async (newPageSize: number, newPage: number) => {
  if (pageSize.value === newPageSize && page.value === newPage) return
  let changed = false
  if (pageSize.value !== newPageSize) {
    pageSize.value = newPageSize
    changed = true
  }
  if (page.value !== newPage) {
    page.value = newPage
    changed = true
  }
  if (changed) await fetchArbitrations()
}

function applyQueryParams() {
  const raw = route.query.status
  const queryStatus = Array.isArray(raw) ? raw[0] : raw
  if (queryStatus && typeof queryStatus === 'string') {
    selectedStatusFilter.value = queryStatus
    statusFilter.value = queryStatus
    filters.value = { ...filters.value, status: { filterType: 'text', filter: queryStatus, type: 'equals' } }
  }
}

const onArbitrationUpdated = () => {
  fetchArbitrations()
}

onMounted(async () => {
  if (!boostCoreStore.user) await boostCoreStore.fetchMe()
  applyQueryParams()
  await fetchArbitrations()
})

provide('onShowArbitrationDetail', async (arbitration: any) => {
  const full = await arbitrationStore.fetchArbitration(arbitration.uuid)
  selectedArbitration.value = full || arbitration
  isDetailModalOpen.value = true
})

watch(searchQuery, () => {
  page.value = 1
  if (timeout) clearTimeout(timeout)
  timeout = setTimeout(() => fetchArbitrations(), 500)
})

watch(filterDateCreation, () => {
  page.value = 1
  fetchArbitrations()
})

watch(statusFilter, async () => {
  page.value = 1
  await fetchArbitrations()
})
</script>
